import { useEffect, useState } from "react";
import { api, fmtDateTime, shortId } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { ProductionBadge } from "@/components/ds/ProductionBadge";
import { Button } from "@/components/ui/button";

export default function Connections() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState({});
    const [myob, setMyob] = useState(null);

    const load = () =>
        Promise.all([
            api.get("/connections").then((r) => setData(r.data)),
            api.get("/myob/status").then((r) => setMyob(r.data)),
        ]);

    useEffect(() => {
        load();
    }, []);

    const startMyobOAuth = async (entityId) => {
        try {
            const { data } = await api.post("/myob/oauth/start", { entity_id: entityId });
            window.open(data.authorize_url, "_blank", "noopener");
            toast.success("MYOB authorisation opened in a new tab");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start MYOB OAuth");
        }
    };

    const sync = async (entityId, sourceSystem) => {
        const key = `${entityId}-${sourceSystem}`;
        setBusy((b) => ({ ...b, [key]: true }));
        try {
            const { data } = await api.post("/connections/sync", {
                entity_id: entityId,
                source_system: sourceSystem,
            });
            toast.success(`Sync complete — ${data.records_synced} records`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally {
            setBusy((b) => ({ ...b, [key]: false }));
        }
    };

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading connections…" testId="connections-loading" />
            </PageShell>
        );

    const myobConfigured = !!myob?.configured;

    return (
        <PageShell width="wide" testId="connections-page">
            <PageHeader
                eyebrow="Integrations"
                title="Connections"
                subtitle="MYOB and bank-feed integrations. Each entity stays on demo data until live credentials are connected."
            />

            <Card variant="quiet" padding="md" className="mb-5" testId="myob-status-banner">
                <div className="flex items-center gap-2 text-[13px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${myobConfigured ? "bg-emerald-500" : "bg-amber-500 pulse-dot"}`} />
                    <span className="text-stone-700">
                        {myobConfigured ? (
                            <>
                                <span className="font-medium">MYOB production OAuth configured.</span> Redirect URI:{" "}
                                <span className="font-mono">{myob.redirect_uri}</span>
                            </>
                        ) : (
                            <>
                                <span className="font-medium">MYOB production OAuth not configured.</span> Set the MYOB
                                client ID, secret and redirect URI in backend env to enable live entities.
                            </>
                        )}
                    </span>
                </div>
            </Card>

            <Section title="Entity integrations">
                <Card variant="default" padding="none" className="overflow-hidden">
                    <table className="w-full text-[14px]">
                        <thead>
                            <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium">Entity</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">State</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">MYOB</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Bank feed</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Last sync</th>
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.entities.map((e) => {
                                const myob = data.connections.find(
                                    (c) => c.entity_id === e.id && c.source_system === "myob",
                                );
                                const bank = data.connections.find(
                                    (c) => c.entity_id === e.id && c.source_system === "bank_feed",
                                );
                                return (
                                    <tr key={e.id} className="border-b border-stone-100 last:border-0" data-testid={`conn-row-${e.entity_number}`}>
                                        <td className="px-5 py-3.5 font-medium text-stone-800">{e.display_name}</td>
                                        <td className="px-3 py-3.5">
                                            <ProductionBadge state={e.data_mode || "mock"} />
                                        </td>
                                        <td className="px-3 py-3.5">
                                            <ConnPill status={myob?.status} />
                                        </td>
                                        <td className="px-3 py-3.5">
                                            <ConnPill status={bank?.status} />
                                        </td>
                                        <td className="px-3 py-3.5 font-mono-num text-[12px] text-stone-500">
                                            {fmtDateTime(e.last_sync_at)}
                                        </td>
                                        <td className="px-5 py-3.5 text-right">
                                            <div className="inline-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-md h-7 text-[12px]"
                                                    disabled={busy[`${e.id}-myob`]}
                                                    onClick={() => sync(e.id, "myob")}
                                                    data-testid={`sync-myob-${e.entity_number}`}
                                                >
                                                    {busy[`${e.id}-myob`] ? "Syncing…" : "Sync MYOB"}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-md h-7 text-[12px]"
                                                    disabled={busy[`${e.id}-bank_feed`]}
                                                    onClick={() => sync(e.id, "bank_feed")}
                                                    data-testid={`sync-bank-${e.entity_number}`}
                                                >
                                                    {busy[`${e.id}-bank_feed`] ? "Syncing…" : "Sync bank"}
                                                </Button>
                                                {myobConfigured && (
                                                    <Button
                                                        size="sm"
                                                        className="rounded-md h-7 text-[12px]"
                                                        style={{ background: "var(--brand)", color: "white" }}
                                                        onClick={() => startMyobOAuth(e.id)}
                                                        data-testid={`connect-myob-${e.entity_number}`}
                                                    >
                                                        Connect
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            </Section>

            <Section title="Recent sync jobs">
                <Card variant="default" padding="none" className="overflow-hidden">
                    <table className="w-full text-[14px]">
                        <thead>
                            <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium">Job</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Entity</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Source</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Status</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Records</th>
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium text-right">Finished</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent_sync_jobs.slice(0, 20).map((j) => {
                                const e = data.entities.find((x) => x.id === j.entity_id);
                                return (
                                    <tr key={j.id} className="border-b border-stone-100 last:border-0">
                                        <td className="px-5 py-2.5 font-mono-num text-[12px] text-stone-500">{shortId(j.id)}</td>
                                        <td className="px-3 py-2.5 text-stone-800">{e?.display_name || "—"}</td>
                                        <td className="px-3 py-2.5 uppercase text-[11px] tracking-wider text-stone-600">{j.source_system}</td>
                                        <td className="px-3 py-2.5 capitalize text-stone-700">{j.status}</td>
                                        <td className="px-3 py-2.5 font-mono-num text-right text-stone-700">{j.records_synced}</td>
                                        <td className="px-5 py-2.5 font-mono-num text-right text-[12px] text-stone-500">
                                            {fmtDateTime(j.finished_at)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            </Section>
        </PageShell>
    );
}

function ConnPill({ status }) {
    if (!status)
        return (
            <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border rounded-full border-stone-200 text-stone-500 bg-white">
                Not connected
            </span>
        );
    const conn =
        status === "connected"
            ? "bg-emerald-50 text-emerald-800 border-emerald-100"
            : "bg-stone-100 text-stone-700 border-stone-200";
    return (
        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border rounded-full ${conn}`}>
            {status.replace("_", " ")}
        </span>
    );
}
